import React from 'react';
import { CaretDown } from '@phosphor-icons/react';
import { fixMojibake } from '../../../../../../utils/text/fix-mojibake';
import { normalizeLiteralEscapeSequences } from '../../../../../../utils/text/normalize-literal-escape-sequences';
import { Msg } from '../../../../../../i18n-review/client/Msg';

export interface EditorialDescriptionProps {
  tldr?: string;
  displayDescription?: string;
  expanded: boolean;
  onToggleExpanded: () => void;
  /** Company's real brand colour; used for the expand/collapse toggle instead of the generic jc-accent green. */
  brandColor?: string;
}

/**
 * TEC-512 editorial-rail redesign: unlike the control layout's either/or toggle (tldr OR full
 * text), the TL;DR always leads here when one exists, with the verbatim original description
 * collapsible underneath it. Defaults to `expanded: true` (owned by the parent, same
 * displayJob.objectID-keyed reset as the control layout's descriptionExpanded) so the SSR HTML
 * always carries the full original text; collapsing is a client-side visual toggle only, never
 * a reason the initial render omits content, per the ticket's "no tabs/accordions that hide
 * content from crawlers" hard requirement.
 */
export function EditorialDescription({ tldr, displayDescription, expanded, onToggleExpanded, brandColor }: EditorialDescriptionProps): React.ReactElement | null {
  if (!tldr && !displayDescription) return null;

  const cleanTldr = tldr ? fixMojibake(normalizeLiteralEscapeSequences(tldr)) : null;
  const cleanFullText = displayDescription ? fixMojibake(displayDescription) : null;

  return (
    <div className="border-b border-[rgba(22,21,15,0.08)] dark:border-[#4A3520]/70 pb-4">
      {cleanTldr ? (
        <>
          <div className="mb-2.5 text-[10.5px] font-extrabold uppercase tracking-wide text-[#8C8474] border-b border-[rgba(22,21,15,0.08)] dark:border-[#4A3520]/70 pb-1.5">
            <Msg id="jobDetailPageV2.tldr" defaultMessage="At a glance" description="Heading for the AI-generated TL;DR summary section on the redesigned job detail page" />
          </div>
          <p className="m-0 mb-2.5 text-[13px] leading-relaxed font-semibold text-[#16150F] dark:text-[#F3EFE4] whitespace-pre-line">
            {cleanTldr}
          </p>
        </>
      ) : null}

      {cleanFullText ? (
        <>
          {/* Without a TL;DR the full text is the only content, so it leads under a plain
              "Job description" heading and the collapse toggle (which would leave an empty
              block) is not offered. With a TL;DR the original text sits under it, collapsible. */}
          {cleanTldr ? (
            <button
              type="button"
              onClick={onToggleExpanded}
              aria-expanded={expanded}
              className={`inline-flex items-center gap-1 text-[12px] font-bold cursor-pointer hover:underline underline-offset-2 ${brandColor ? '' : 'text-jc-accent'}`}
              style={brandColor ? { color: brandColor } : undefined}
            >
              <CaretDown className={`h-2.5 w-2.5 transition-transform ${expanded ? '' : '-rotate-90'}`} aria-hidden />
              {/* Both labels stay mounted, toggled via `hidden` instead of a ternary that swaps
                  which Msg element occupies this slot: Google Translate rewrites live text nodes
                  into <font> wrappers, and a React re-render that then removes/replaces one of
                  those nodes in place (rather than just flipping a class on an untouched node) is
                  the classic trigger for a `removeChild`/`insertBefore` crash. Same reasoning below
                  for the collapsible original-description block. */}
              <span className={expanded ? undefined : 'hidden'} aria-hidden={expanded ? undefined : true}>
                <Msg id="jobDetailPageV2.hideOriginalDescription" defaultMessage="Hide original description" description="Button that collapses the verbatim original job description, editorial-rail redesign" />
              </span>
              <span className={expanded ? 'hidden' : undefined} aria-hidden={expanded ? true : undefined}>
                <Msg id="jobDetailPageV2.readFullDescription" defaultMessage="Read full description" description="Button that expands the AI TL;DR summary into the full job description" />
              </span>
            </button>
          ) : null}
          <div
            className={cleanTldr ? `mt-3.5 pt-3.5 border-t border-dashed border-[rgba(22,21,15,0.15)] dark:border-[#4A3520]/70 ${expanded ? '' : 'hidden'}` : ''}
            aria-hidden={cleanTldr && !expanded ? true : undefined}
          >
            <div className={`mb-2 text-[10.5px] font-extrabold uppercase tracking-wide text-[#8C8474] ${cleanTldr ? '' : 'border-b border-[rgba(22,21,15,0.08)] dark:border-[#4A3520]/70 pb-1.5 mb-2.5'}`}>
              <span className={cleanTldr ? undefined : 'hidden'}>
                <Msg id="jobDetailPageV2.originalDescription" defaultMessage="Original job description" description="Heading for the verbatim original job description, editorial-rail redesign" />
              </span>
              <span className={cleanTldr ? 'hidden' : undefined}>
                <Msg id="jobDetailPageV2.jobDescription" defaultMessage="Job description" description="Heading over the employer's job description when the listing has no TL;DR summary, editorial-rail redesign" />
              </span>
            </div>
            <p className="m-0 text-[12.5px] leading-loose text-[#4A4438] dark:text-[#C4A882] whitespace-pre-line">
              {cleanFullText}
            </p>
            {/* Provenance note as a footnote under the text, not a preamble the reader has to get
                past before the content: it matters once someone compares the structured fields
                against the original wording, not before they have read either. */}
            <p className="m-0 mt-3 text-[10.5px] leading-relaxed text-[#8C8474] dark:text-[#9C9384]">
              <Msg
                id="jobDetailPageV2.descriptionDisclaimer"
                defaultMessage="The text above is the employer's original job description, extracted as written. Other details on this page, like salary, responsibilities, and requirements, are interpreted from that text by our system, not values the employer explicitly confirmed, so treat them as our best interpretation rather than verified facts."
                description="Small provenance note under the job description, distinguishing the verbatim original description from structured fields elsewhere on the page (salary, responsibilities, etc.) that are LLM-interpreted from that same text, not directly confirmed by the employer"
              />
            </p>
          </div>
        </>
      ) : null}
    </div>
  );
}
